import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import MultiSelect from '../components/ContactCenter/MultiSelect';
import { fmtCount, fmtPct } from '../components/ContactCenter/format';
import { btn, c, card, tbl } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · City — where calls come from and why: volume by pod, by the
// caller's city, and by wrap-up code, drilled one level at a time
// (services/ccCity.js). Dates are Pacific (the old page used the browser's).

const CHANNELS = ['Voice', 'VIP', 'Callback', 'Email', 'Chat', 'Voicemail'];
const DEFAULT = ['Voice', 'VIP', 'Callback'];
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}

export default function ContactCenterCity() {
  const today = pacificToday();
  const [mode, setMode] = useState('mtd');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [channels, setChannels] = useState(DEFAULT);
  const [abandoned, setAbandoned] = useState(false);
  const [view, setView] = useState('overview');
  const [drill, setDrill] = useState({ pod: null, city: null });
  // The pod FILTER, which is not the pod drill-down below it. The filter scopes
  // the whole page — the resolution stats, the breakdown, the totals every
  // percentage is taken of. Drilling picks one pod to read cities for, inside
  // whatever the filter left. Empty means every pod, so no list is sent.
  const [pods, setPods] = useState([]);

  const range = mode === 'today' ? { start: today, end: today }
    : mode === 'custom' ? (custom.start && custom.end ? custom : null)
      : { start: `${today.slice(0, 8)}01`, end: today };
  const params = range && {
    ...range, channels: (channels.length ? channels : DEFAULT).join(','), includeAbandoned: String(abandoned),
    ...(view === 'breakdown' ? { mode: 'breakdown' } : {}),
    ...(pods.length ? { pods: pods.join(',') } : {}),
    ...(drill.pod ? { pod: drill.pod } : {}), ...(drill.city ? { city: drill.city } : {}),
  };
  const q = useQuery({
    queryKey: ['cc-city', JSON.stringify(params)],
    queryFn: () => api.get('/contact-center/city', { params }),
    enabled: !!range,
    refetchInterval: 5 * 60 * 1000,
  });
  const d = q.data;
  // Any filter change starts again from the top, as the old page did.
  const reset = (fn) => (...a) => { setDrill({ pod: null, city: null }); fn(...a); };

  return (
    <div style={s.page}>
      <PageHeader eyebrow="Contact Center" title="City Insights"
        subtitle={d ? `${d.window.start} to ${d.window.end} · ${fmtCount(d.summary.total)} conversations · volume → pod → city → reason` : 'Volume → pod → city → reason'}
        actions={(
          <div style={s.row} role="group" aria-label="Window">
            {[['today', 'Today'], ['mtd', 'Month to date'], ['custom', 'Custom']].map(([k, l]) => (
              <button key={k} type="button" aria-pressed={mode === k} onClick={reset(() => setMode(k))} style={mode === k ? s.pillOn : s.pill}>{l}</button>
            ))}
            {mode === 'custom' && (
              <>
                <input id="cc-city-start" type="date" aria-label="Start date" min={d?.dataFloor || '2026-05-19'} max={today} value={custom.start}
                  onChange={(e) => { setDrill({ pod: null, city: null }); setCustom((v) => ({ ...v, start: e.target.value })); }} style={s.input} />
                <input id="cc-city-end" type="date" aria-label="End date" min={d?.dataFloor || '2026-05-19'} max={today} value={custom.end}
                  onChange={(e) => { setDrill({ pod: null, city: null }); setCustom((v) => ({ ...v, end: e.target.value })); }} style={s.input} />
              </>
            )}
          </div>
        )} />
      <div style={s.row} role="group" aria-label="Filters">
        {/* align="left": this row is left-aligned and in the page body, so a
            menu hung off the button's right edge would open under the sidebar. */}
        <MultiSelect label="Pod" options={d?.allPods || []} selected={pods} onChange={reset(setPods)}
          allLabel="All" noun="pods" emptyMeansAll align="left" />
        {CHANNELS.map((ch) => (
          <button key={ch} type="button" aria-pressed={channels.includes(ch)} onClick={reset(() => setChannels((cur) => toggle(cur, ch)))}
            style={channels.includes(ch) ? s.pillOn : s.pill}>{ch}</button>
        ))}
        <label style={s.check}><input type="checkbox" checked={abandoned} onChange={reset((e) => setAbandoned(e.target.checked))} /> Include abandoned</label>
        <span style={{ marginLeft: 'auto' }}>
          {[['overview', 'By city'], ['breakdown', 'Reasons & cities']].map(([k, l]) => (
            <button key={k} type="button" aria-pressed={view === k} onClick={reset(() => setView(k))} style={view === k ? s.pillOn : s.pill}>{l}</button>
          ))}
        </span>
      </div>

      {mode === 'custom' && !range && <p style={s.sub}>Choose a start and an end date.</p>}
      {q.isLoading && range && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load City: {q.error.message}</div>}

      {d && !drill.pod && (
        <section style={s.card} aria-label="Overall volume">
          <div style={s.sectionHead}>
            <h2 style={s.sectionTitle}>Overall volume</h2>
            <span style={s.sectionSub}>Resolution quality across the recent window</span>
          </div>
          <div style={s.stats}>
            <div><div style={s.big}>{fmtCount(d.summary.total)}</div><div style={s.sub}>Conversations</div></div>
            <div><div style={{ ...s.big, color: c.successText }}>{fmtPct(d.summary.companyPct)}</div><div style={s.sub}>Via CompanyId · {fmtCount(d.summary.byCompany)}</div></div>
            <div><div style={{ ...s.big, color: c.warningText }}>{fmtPct(d.summary.zipPct)}</div><div style={s.sub}>Via zip lookup · {fmtCount(d.summary.byZip)}</div></div>
            <div><div style={{ ...s.big, color: d.summary.unknownPct > 20 ? c.dangerText : c.warningText }}>{fmtPct(d.summary.unknownPct)}</div><div style={s.sub}>Unknown · {fmtCount(d.summary.unknown)}</div></div>
          </div>
          {d.summary.pending > 0 && (
            <p style={s.notice}>
              {fmtCount(d.summary.pending)} conversations since September 18 have no city yet: the portal&rsquo;s Genesys access cannot read
              the caller&rsquo;s account or zip until single-conversation access is granted. They count in volume and reasons, not in cities.
            </p>
          )}
        </section>
      )}

      {d && view === 'overview' && !drill.pod && (
        <section style={s.card} aria-label="Pod breakdown">
          <div style={s.sectionHead}>
            <h2 style={s.sectionTitle}>Pod breakdown</h2>
            <span style={s.sectionSub}>Click a pod to drill into its cities</span>
          </div>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Pod</th><th style={s.thNum}>Conversations</th><th style={s.thNum}>% of total</th><th style={s.th}>Top city</th><th style={s.th}>Top reason</th></tr></thead>
            <tbody>{d.pods.map((p) => (
              <tr key={p.pod} style={s.click} onClick={() => setDrill({ pod: p.pod, city: null })}>
                <td style={{ ...s.td, fontWeight: 600 }}>{p.pod}</td><td style={s.num}>{fmtCount(p.conversations)}</td><td style={s.num}>{fmtPct(p.pctOfTotal)}</td>
                <td style={s.td}>{p.topCity || '—'}{p.topCityIsNotFirst ? <span style={s.sub}> (top known)</span> : ''}</td><td style={s.td}>{p.topWrapup || '—'}</td>
              </tr>))}
            </tbody>
          </table>
        </section>
      )}

      {d?.pod && !drill.city && (
        <section style={s.card} aria-label={`${d.pod.pod} cities`}>
          <div style={s.crumbs}><button type="button" style={btn.linkXs} onClick={() => setDrill({ pod: null, city: null })}>All pods</button> › {d.pod.pod}</div>
          <table style={s.table}>
            <thead><tr><th style={s.th}>City</th><th style={s.thNum}>Conversations</th><th style={s.thNum}>% of pod</th><th style={s.th}>Found by</th><th style={s.th}>Top reason</th></tr></thead>
            <tbody>{d.pod.cities.map((ct) => (
              <tr key={ct.cityKey} style={s.click} onClick={() => setDrill({ pod: d.pod.pod, city: ct.cityKey })}>
                <td style={s.td}>{ct.city}{ct.mostlyZip ? <span style={s.sub} title="Mostly placed by zip code"> ⚑</span> : ''}</td>
                <td style={s.num}>{fmtCount(ct.conversations)}</td><td style={s.num}>{fmtPct(ct.pctOfPod)}</td>
                <td style={s.td}>{ct.cityKey === 'UNKNOWN' ? '—' : `${fmtPct(ct.companyPct)} account · ${fmtPct(ct.zipPct)} zip`}</td><td style={s.td}>{ct.topWrapup || '—'}</td>
              </tr>))}
            </tbody>
          </table>
        </section>
      )}

      {d?.city && (
        <section style={s.card} aria-label="Reasons">
          <div style={s.crumbs}>
            <button type="button" style={btn.linkXs} onClick={() => setDrill({ pod: null, city: null })}>All pods</button> ›{' '}
            <button type="button" style={btn.linkXs} onClick={() => setDrill({ pod: d.city.pod, city: null })}>{d.city.pod}</button> › {d.city.cityKey}
          </div>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Wrap-up code</th><th style={s.thNum}>Count</th><th style={s.thNum}>% of city</th><th style={s.th} /></tr></thead>
            <tbody>{d.city.wrapups.map((w) => (
              <tr key={w.name}><td style={s.td}>{w.name}</td><td style={s.num}>{fmtCount(w.count)}</td><td style={s.num}>{fmtPct(w.pct)}</td>
                <td style={s.td}><span style={{ ...s.bar, width: `${w.pct || 0}%` }} /></td></tr>))}
            </tbody>
          </table>
        </section>
      )}

      {d?.breakdown && !drill.pod && (
        <div style={s.grid} aria-label="Reasons and cities by pod">
          {d.breakdown.map((p) => (
            <section key={p.pod} style={s.card} aria-label={`${p.pod} reasons and cities`}>
              <h2 style={s.h2}>{p.pod} · {fmtCount(p.conversations)}</h2>
              <p style={s.label}>Reasons</p>
              {p.wrapups.slice(0, 5).map((w) => <div key={w.name} style={s.line}><span>{w.name}</span><span>{fmtPct(w.pct)}</span></div>)}
              <p style={s.label}>Cities</p>
              {p.cities.slice(0, 5).map((ct) => <div key={ct.city} style={s.line}><span>{ct.city}</span><span>{fmtPct(ct.pct)}</span></div>)}
            </section>
          ))}
        </div>
      )}
      {d && (
        <p style={s.foot}>
          Inbound conversations; abandoned calls {abandoned ? 'included' : 'left out'}. A city comes from the caller&rsquo;s account where the
          IVR found one, otherwise their zip code (⚑ when a city is placed mostly by zip). &ldquo;Top reason&rdquo; skips codes that are not a
          reason for calling (no disposition, no answer, the default code); a city&rsquo;s full list shows every code.
        </p>
      )}
    </div>
  );
}

const s = {
  sectionHead:  { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' },
  sectionTitle: { fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: c.textMuted, margin: 0 },
  sectionSub:   { fontSize: '12px', color: c.textFaint },
  page:   { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  row:    { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  pill:   { ...btn.secondaryXs },
  pillOn: { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  input:  { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong },
  check:  { fontSize: '12px', color: c.textStrong, marginLeft: '8px' },
  card:   { ...card.base, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '8px' },
  stats:  { display: 'flex', gap: '36px', flexWrap: 'wrap' },
  big:    { fontSize: '26px', fontWeight: 650, color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  sub:    { fontSize: '11.5px', color: c.textMuted, margin: 0 },
  notice: { fontSize: '12px', color: c.warningText, background: c.warningTint, padding: '8px 12px', borderRadius: '8px', margin: 0 },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:     { ...tbl.thSm, textAlign: 'left' },
  thNum:  { ...tbl.thRSm },
  td:     { ...tbl.tdSm, color: c.textStrong },
  num:    { ...tbl.tdRSm, color: c.textStrong },
  click:  { ...tbl.trHover },
  crumbs: { fontSize: '12.5px', color: c.textStrong },
  bar:    { display: 'inline-block', height: '8px', background: c.navy, borderRadius: '4px' },
  grid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' },
  h2:     { fontSize: '13px', fontWeight: 650, color: c.text, margin: 0 },
  label:  { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted, margin: '4px 0 0' },
  line:   { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: c.textStrong },
  error:  { fontSize: '13px', color: c.dangerText },
  foot:   { fontSize: '11px', color: c.textMuted, margin: 0, lineHeight: 1.5 },
};
